/// <amd-module name="Wit/DeliveryPlans/DependencyPanel/DependencyService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { WorkItemDependencyData, WorkItemDependencyPanelData } from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
export interface IDependencyService extends IVssService {
    clear(): void;
    getDependencies(workItemId: number, ignoreCache?: boolean): Promise<WorkItemDependencyPanelData | undefined>;
    getWorkItemsDependencies(workItemIds: number[], ignoreCache?: boolean): Promise<WorkItemsDependency | undefined>;
    getDependenciesErrorMessage(): string | undefined;
}

export interface WorkItemDependencyItem {
    workItemId: number;
    dependencyData: WorkItemDependencyData[];
}

export interface WorkItemsDependency {
    dependencies: WorkItemDependencyItem[];
}

const DependencyDataProvider = "ms.vss-work-web.delivery-plans-dependency-graph-data-provider";
const BatchDependencyDataProvider = "ms.vss-work-web.delivery-plans-dependency-graph-batch-data-provider";

class DependencyService extends VssService implements IDependencyService {
    private _workItemDependencyCache: { [workItemId: number]: Promise<WorkItemDependencyPanelData | undefined> } = {};
    private _workItemsDependenciesCache: { cachedWorkItemIds: number[]; dependencies: Promise<WorkItemsDependency | undefined> | undefined } = {
        cachedWorkItemIds: [],
        dependencies: undefined
    };

    public clear() {
        this._workItemDependencyCache = {};
    }

    public async getDependencies(workItemId: number, ignoreCache?: boolean): Promise<WorkItemDependencyPanelData | undefined> {
        if (!this._workItemDependencyCache[workItemId] || ignoreCache) {
            this._workItemDependencyCache[workItemId] = this._loadDependencies(workItemId);
        }

        return this._workItemDependencyCache[workItemId];
    }

    public async getWorkItemsDependencies(workItemIds: number[], ignoreCache?: boolean): Promise<WorkItemsDependency | undefined> {
        if (ignoreCache || this._workItemsDependenciesCache?.cachedWorkItemIds.length === 0) {
            this._workItemsDependenciesCache = {
                cachedWorkItemIds: workItemIds,
                dependencies: this._loadDependenciesBatch(workItemIds)
            };
        } else {
            const notCached = workItemIds.filter(id => this._workItemsDependenciesCache.cachedWorkItemIds.indexOf(id) === -1);
            if (notCached.length > 0) {
                const newCache: Promise<WorkItemsDependency> = new Promise(async (resolve, reject) => {
                    const [old, diff] = await Promise.all([this._workItemsDependenciesCache.dependencies, this._loadDependenciesBatch(notCached)]);
                    if (old && diff) {
                        const mergedDependencies: WorkItemsDependency = {
                            dependencies: old.dependencies.concat(diff.dependencies)
                        };
                        resolve(mergedDependencies);
                    } else {
                        reject;
                    }
                });

                this._workItemsDependenciesCache = {
                    cachedWorkItemIds: this._workItemsDependenciesCache.cachedWorkItemIds.concat(notCached),
                    dependencies: newCache
                };
            }
        }

        return this._workItemsDependenciesCache.dependencies;
    }

    public getDependenciesErrorMessage(): string | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const exception = contributionService.getDataProviderExceptions()[DependencyDataProvider];
        return exception?.message;
    }

    private async _loadDependencies(workItemId: number): Promise<WorkItemDependencyPanelData | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<WorkItemDependencyPanelData>(
            DependencyDataProvider,
            {
                workItemId
            },
            /** ignoreCache */ true
        );
    }

    private async _loadDependenciesBatch(workItemIds: number[]): Promise<WorkItemsDependency | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<WorkItemsDependency>(
            BatchDependencyDataProvider,
            {
                workItemIds
            },
            /** ignoreCache */ true
        );
    }
}

const serviceName = "IDependencyService";
Services.add(serviceName, { serviceFactory: DependencyService });
